// Standardized event names — the contract practices wire their GTM triggers to.
export const BOOKING_STARTED_EVENT = "physiofit_booking_started";
export const BOOKING_COMPLETED_EVENT = "physiofit_booking_completed";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;
const CLICK_ID_KEYS = ["gclid", "fbclid"] as const;

export type CapturedTrackingParams = Partial<
  Record<(typeof UTM_KEYS)[number] | (typeof CLICK_ID_KEYS)[number], string>
>;

type DataLayerObject = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerObject[];
  }
}

// Captured once per page load; kept out of the persisted Zustand store on purpose,
// so a fresh visit never inherits a previous session's attribution.
let capturedParams: CapturedTrackingParams | null = null;
let gtmLoadedContainerId: string | null = null;

/** Read UTM + click-id params from the current browser URL. Caches on first call. */
export const captureTrackingParams = (): CapturedTrackingParams => {
  if (capturedParams) return capturedParams;
  const search = new URLSearchParams(window.location.search);
  const params: CapturedTrackingParams = {};
  [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = search.get(key);
    if (value) params[key] = value;
  });
  capturedParams = params;
  return params;
};

/**
 * Idempotently inject a GTM container. Used only on the standalone booking page,
 * where no host GTM exists; the embedded widget relies on the host site's own GTM.
 */
export const ensureGtmLoaded = (containerId: string): void => {
  if (!containerId || gtmLoadedContainerId === containerId) return;
  if (document.getElementById("physiofit-gtm")) {
    gtmLoadedContainerId = containerId;
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.id = "physiofit-gtm";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    containerId,
  )}`;
  document.head.appendChild(script);
  gtmLoadedContainerId = containerId;
};

/** Push a booking lifecycle event to the (host or injected) GTM dataLayer. */
export const pushBookingEvent = (
  event: typeof BOOKING_STARTED_EVENT | typeof BOOKING_COMPLETED_EVENT,
  payload: DataLayerObject,
): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...captureTrackingParams(),
    ...payload,
  });
};
